import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { LOGO_PATH, BUSINESS_NAME, SUPPORT_EMAIL, SUPPORT_WHATSAPP } from '../config/brand';
import { Scissors, Sparkles, Heart, Shield, Clock, Star, ChevronRight, Phone, Mail, MapPin, MessageCircle } from 'lucide-react';

const HERO_IMG = "https://images.unsplash.com/photo-1736196674354-b5e918a64644?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzV8MHwxfHNlYXJjaHwyfHxoYXBweSUyMGRvZyUyMHBvcnRyYWl0JTIwcHJvZmVzc2lvbmFsJTIwc3R1ZGlvJTIwbGlnaHRpbmclMjB3aGl0ZSUyMGJhY2tncm91bmR8ZW58MHx8fHwxNzczMzEyNzYyfDA&ixlib=rb-4.1.0&q=85";
const GROOMING_IMG = "https://images.unsplash.com/photo-1647002380358-fc70ed2f04e0?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NDh8MHwxfHNlYXJjaHwyfHxkb2clMjBncm9vbWluZyUyMGJhdGglMjBidWJibGVzJTIwcHJvZmVzc2lvbmFsfGVufDB8fHx8MTc3MzMxMjc3Mnww&ixlib=rb-4.1.0&q=85";
const STYLING_IMG = "https://images.unsplash.com/photo-1547999825-2e97e08d6757?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzV8MHwxfHNlYXJjaHw0fHxoYXBweSUyMGRvZyUyMHBvcnRyYWl0JTIwcHJvZmVzc2lvbmFsJTIwc3R1ZGlvJTIwbGlnaHRpbmclMjB3aGl0ZSUyMGJhY2tncm91bmR8ZW58MHx8fHwxNzczMzEyNzYyfDA&ixlib=rb-4.1.0&q=85";

const services = [
  { icon: Scissors, title: "Full Service Grooming", desc: "Complete grooming with bath, haircut, styling, nail trim, and finishing", price: "\u20B92,250" },
  { icon: Sparkles, title: "Bath & Dry", desc: "Premium bath with shampoo, blow dry, and brushout for a fresh coat", price: "\u20B91,500" },
  { icon: Heart, title: "Premium Coat Conditioning", desc: "Deep conditioning for a silky, healthy, and shiny coat", price: "\u20B91,800" },
  { icon: Shield, title: "Anti-Tick & Flea", desc: "Thorough treatment with medicated products for pest removal", price: "\u20B91,900" },
];

const features = [
  { icon: Clock, title: "Easy Online Booking", desc: "Book appointments 24/7 from any device" },
  { icon: Star, title: "Expert Groomers", desc: "Certified professionals who love what they do" },
  { icon: Shield, title: "Health-First Approach", desc: "Vaccination tracking and allergy awareness" },
  { icon: Heart, title: "Pet-Centered Care", desc: "Stress-free environment designed for pets" },
];

export default function Landing() {
  const navigate = useNavigate();
  const { user } = useAuth();

  const handleServiceClick = (serviceTitle) => {
    if (user) {
      navigate(`/client/book?service=${encodeURIComponent(serviceTitle)}`);
    } else {
      navigate(`/signup?service=${encodeURIComponent(serviceTitle)}`);
    }
  };

  return (
    <div className="min-h-screen bg-[#FBF7F3]">
      {/* Navigation */}
      <nav className="sticky top-0 z-50 glass" data-testid="landing-nav">
        <div className="max-w-7xl mx-auto px-6 md:px-12 flex items-center justify-between h-16">
          <div className="flex items-center gap-2 cursor-pointer" onClick={() => navigate('/')}>
            <img src={LOGO_PATH} alt={BUSINESS_NAME} className="h-10" />
          </div>
          <div className="hidden md:flex items-center gap-8">
            <a href="#services" className="text-sm text-[#7A6B5E] hover:text-[#6A3E28] font-medium">Services</a>
            <a href="#features" className="text-sm text-[#7A6B5E] hover:text-[#6A3E28] font-medium">Why {BUSINESS_NAME}</a>
            <a href="#contact" className="text-sm text-[#7A6B5E] hover:text-[#6A3E28] font-medium">Contact</a>
          </div>
          <div className="flex items-center gap-3">
            <Button variant="ghost" className="text-sm font-medium text-[#6A3E28]" onClick={() => navigate('/login')} data-testid="login-btn">
              Log in
            </Button>
            <Button className="rounded-full bg-[#6A3E28] text-white hover:bg-[#5A3220] px-6 text-sm shadow-md hover:-translate-y-0.5 active:translate-y-0" onClick={() => navigate('/signup')} data-testid="signup-btn" style={{ transition: 'all 0.2s ease' }}>
              Get Started
            </Button>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="hero-gradient py-20 md:py-32 px-6 md:px-12">
        <div className="max-w-7xl mx-auto grid md:grid-cols-2 gap-12 items-center">
          <div className="animate-fade-in-up">
            <p className="text-sm font-semibold text-[#C48C68] tracking-wide uppercase mb-4">Premium Pet Grooming</p>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-[#3D2418] tracking-tight leading-tight" style={{ fontFamily: 'Manrope' }}>
              Where Every Pet<br />Gets the Royal<br />Treatment
            </h1>
            <p className="mt-6 text-base md:text-lg text-[#7A6B5E] leading-relaxed max-w-lg">
              Book grooming appointments, manage pet profiles, and give your furry friends the care they deserve. All in one beautiful platform.
            </p>
            <div className="mt-8 flex flex-wrap gap-4">
              <Button className="rounded-full bg-[#6A3E28] text-white hover:bg-[#5A3220] px-8 py-6 text-base shadow-lg hover:-translate-y-0.5 active:translate-y-0" onClick={() => navigate('/signup')} data-testid="hero-cta-btn" style={{ transition: 'all 0.2s ease' }}>
                Book Now <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
              <Button variant="outline" className="rounded-full border-2 border-[#C48C68]/40 text-[#6A3E28] px-8 py-6 text-base hover:bg-[#C48C68]/10" onClick={() => navigate('/login')} data-testid="hero-login-btn">
                View Services
              </Button>
            </div>
            <div className="mt-10 flex items-center gap-8">
              <div><p className="text-2xl font-bold text-[#6A3E28]">2,500+</p><p className="text-xs text-[#A09387]">Happy Pets</p></div>
              <div className="w-px h-8 bg-[#E8DDD4]" />
              <div><p className="text-2xl font-bold text-[#6A3E28]">4.9</p><p className="text-xs text-[#A09387]">Star Rating</p></div>
              <div className="w-px h-8 bg-[#E8DDD4]" />
              <div><p className="text-2xl font-bold text-[#6A3E28]">5+</p><p className="text-xs text-[#A09387]">Years Experience</p></div>
            </div>
          </div>
          <div className="relative animate-fade-in-up" style={{ animationDelay: '0.2s' }}>
            <div className="rounded-3xl overflow-hidden shadow-xl" style={{ boxShadow: '0 20px 60px rgba(106,62,40,0.15)' }}>
              <img src={HERO_IMG} alt="Happy dog portrait" className="w-full h-[400px] md:h-[520px] object-cover" />
            </div>
            <div className="absolute -bottom-4 -left-4 bg-white rounded-2xl p-4 shadow-lg" style={{ boxShadow: '0 8px 30px rgba(106,62,40,0.1)' }}>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-[#F5EDE6] rounded-full flex items-center justify-center">
                  <Heart className="w-5 h-5 text-[#A96B49]" strokeWidth={1.5} />
                </div>
                <div>
                  <p className="text-sm font-semibold text-[#6A3E28]">Trusted Care</p>
                  <p className="text-xs text-[#A09387]">100% Satisfaction</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services */}
      <section id="services" className="py-20 md:py-32 px-6 md:px-12">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <p className="text-sm font-semibold text-[#C48C68] tracking-wide uppercase mb-3">Our Services</p>
            <h2 className="text-3xl md:text-4xl font-semibold text-[#3D2418] tracking-tight" style={{ fontFamily: 'Manrope' }}>
              Premium Grooming Services
            </h2>
            <p className="mt-4 text-base text-[#7A6B5E] max-w-xl mx-auto">
              From a quick trim to a full spa day, we offer everything your pet needs to look and feel their best.
            </p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {services.map((svc, i) => (
              <Card
                key={i}
                className="rounded-2xl border-[#E8DDD4] bg-white card-hover cursor-pointer group"
                style={{ animationDelay: `${i * 0.1}s` }}
                onClick={() => handleServiceClick(svc.title)}
                data-testid={`landing-service-${i}`}
              >
                <CardContent className="p-6 md:p-8">
                  <div className="w-12 h-12 bg-[#F5EDE6] rounded-xl flex items-center justify-center mb-5 group-hover:bg-[#6A3E28] group-hover:scale-[1.02]" style={{ transition: 'background-color 0.3s ease, transform 0.3s ease' }}>
                    <svc.icon className="w-6 h-6 text-[#A96B49] group-hover:text-white" strokeWidth={1.5} style={{ transition: 'color 0.3s ease' }} />
                  </div>
                  <h3 className="text-lg font-semibold text-[#3D2418] mb-2">{svc.title}</h3>
                  <p className="text-sm text-[#7A6B5E] mb-4 leading-relaxed">{svc.desc}</p>
                  <p className="text-xl font-bold text-[#A96B49]">{svc.price}</p>
                  <p className="text-xs text-[#A09387] mb-4">starting from · plus 18% GST</p>
                  <span className="inline-flex items-center text-sm font-semibold text-[#6A3E28] group-hover:text-[#A96B49]" style={{ transition: 'color 0.3s ease' }}>
                    Book Now <ChevronRight className="w-4 h-4 ml-1 group-hover:translate-x-1" style={{ transition: 'transform 0.3s ease' }} />
                  </span>
                </CardContent>
              </Card>
            ))}
          </div>
          <div className="mt-12 grid md:grid-cols-2 gap-6">
            <div className="rounded-2xl overflow-hidden" style={{ boxShadow: '0 8px 30px rgba(106,62,40,0.08)' }}>
              <img src={GROOMING_IMG} alt="Dog grooming" className="w-full h-[280px] object-cover" />
            </div>
            <div className="rounded-2xl overflow-hidden" style={{ boxShadow: '0 8px 30px rgba(106,62,40,0.08)' }}>
              <img src={STYLING_IMG} alt="Dog styling" className="w-full h-[280px] object-cover" />
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section id="features" className="py-20 md:py-32 px-6 md:px-12 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <p className="text-sm font-semibold text-[#C48C68] tracking-wide uppercase mb-3">Why Choose Us</p>
            <h2 className="text-3xl md:text-4xl font-semibold text-[#3D2418] tracking-tight" style={{ fontFamily: 'Manrope' }}>
              Built for Pets & Their Humans
            </h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((f, i) => (
              <div key={i} className="text-center">
                <div className="w-14 h-14 bg-[#F5EDE6] rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <f.icon className="w-7 h-7 text-[#6A3E28]" strokeWidth={1.5} />
                </div>
                <h3 className="text-base font-semibold text-[#3D2418] mb-2">{f.title}</h3>
                <p className="text-sm text-[#7A6B5E] leading-relaxed">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 md:py-32 px-6 md:px-12">
        <div className="max-w-4xl mx-auto text-center">
          <div className="bg-[#6A3E28] rounded-3xl p-12 md:p-16" style={{ boxShadow: '0 20px 60px rgba(106,62,40,0.2)' }}>
            <h2 className="text-3xl md:text-4xl font-bold text-white tracking-tight mb-4" style={{ fontFamily: 'Manrope' }}>
              Ready to Pamper Your Pet?
            </h2>
            <p className="text-[#D4B9A5] mb-8 max-w-md mx-auto">
              Join thousands of happy pet owners. Book your first grooming session today.
            </p>
            <Button className="rounded-full bg-white text-[#6A3E28] hover:bg-[#F5EDE6] px-8 py-6 text-base font-semibold shadow-lg hover:-translate-y-0.5 active:translate-y-0" onClick={() => navigate('/signup')} data-testid="cta-signup-btn" style={{ transition: 'all 0.2s ease' }}>
              Create Free Account <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer id="contact" className="bg-white border-t border-[#E8DDD4] py-12 px-6 md:px-12">
        <div className="max-w-7xl mx-auto grid md:grid-cols-3 gap-8">
          <div>
            <div className="flex items-center gap-2 mb-4">
              <img src={LOGO_PATH} alt={BUSINESS_NAME} className="h-8" />
            </div>
            <p className="text-sm text-[#7A6B5E]">Premium pet grooming studio dedicated to making every pet look and feel their best.</p>
          </div>
          <div>
            <h4 className="text-sm font-semibold text-[#6A3E28] mb-4 uppercase tracking-wide">Contact</h4>
            <div className="space-y-3 text-sm text-[#7A6B5E]">
              <div className="flex items-center gap-2"><Phone className="w-4 h-4" strokeWidth={1.5} /> 555-0100</div>
              <div className="flex items-center gap-2"><Mail className="w-4 h-4" strokeWidth={1.5} /> {SUPPORT_EMAIL}</div>
              <div className="flex items-center gap-2"><MapPin className="w-4 h-4" strokeWidth={1.5} /> 123 Paw Street, Pet City</div>
              <a href={`https://wa.me/${SUPPORT_WHATSAPP}?text=${encodeURIComponent(`Hi ${BUSINESS_NAME}! I'd like to enquire about grooming services.`)}`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-[#25D366] hover:text-[#1DA851] font-medium" data-testid="footer-whatsapp-link">
                <MessageCircle className="w-4 h-4" strokeWidth={1.5} /> Chat on WhatsApp
              </a>
            </div>
          </div>
          <div>
            <h4 className="text-sm font-semibold text-[#6A3E28] mb-4 uppercase tracking-wide">Hours</h4>
            <div className="space-y-2 text-sm text-[#7A6B5E]">
              <p>Monday - Friday: 9:00 AM - 6:00 PM</p>
              <p>Saturday: 9:00 AM - 4:00 PM</p>
              <p>Sunday: Closed</p>
            </div>
          </div>
        </div>
        <div className="max-w-7xl mx-auto mt-8 pt-8 border-t border-[#E8DDD4] text-center text-xs text-[#A09387]">
          {new Date().getFullYear()} {BUSINESS_NAME}. All rights reserved.
        </div>
      </footer>
    </div>
  );
}
